import React from 'react';
import { useFracas } from '../contexts/FracasContext';
import {
    AlertTriangle, TrendingUp, Clock,
    AlertCircle, BarChart3, Target, Activity
} from 'lucide-react';

const AnalyticsDashboard: React.FC = () => {
    const { loading, getMetrics } = useFracas();
    const metrics = getMetrics();

    if (loading) {
        return (
            <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    // Calculate additional metrics
    const criticalPercentage = metrics.totalIncidents > 0
        ? Math.round((metrics.criticalIncidents / metrics.totalIncidents) * 100)
        : 0;

    const openPercentage = metrics.totalIncidents > 0
        ? Math.round((metrics.openIncidents / metrics.totalIncidents) * 100)
        : 0;

    return (
        <div className="p-8 max-w-7xl mx-auto">
            <div className="mb-8">
                <h1 className="text-3xl font-bold text-gray-900">Analytics Dashboard</h1>
                <p className="text-gray-600 mt-1">
                    FRACAS performance metrics and insights
                </p>
            </div>

            {/* KPI Cards */}
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                {/* Total Incidents */}
                <div className="bg-white rounded-xl border border-gray-200 p-6">
                    <div className="flex items-center justify-between mb-4">
                        <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center">
                            <BarChart3 className="w-6 h-6 text-blue-600" />
                        </div>
                        <span className="text-sm font-medium text-gray-500">Total</span>
                    </div>
                    <div className="text-3xl font-bold text-gray-900">{metrics.totalIncidents}</div>
                    <p className="text-sm text-gray-600 mt-1">All Incidents</p>
                </div>

                {/* Open Incidents */}
                <div className="bg-white rounded-xl border border-gray-200 p-6">
                    <div className="flex items-center justify-between mb-4">
                        <div className="w-12 h-12 rounded-lg bg-amber-100 flex items-center justify-center">
                            <Activity className="w-6 h-6 text-amber-600" />
                        </div>
                        <span className="text-sm font-medium text-gray-500">Active</span>
                    </div>
                    <div className="text-3xl font-bold text-gray-900">{metrics.openIncidents}</div>
                    <p className="text-sm text-gray-600 mt-1">
                        {openPercentage}% of total
                    </p>
                </div>

                {/* Critical Incidents */}
                <div className="bg-white rounded-xl border border-gray-200 p-6">
                    <div className="flex items-center justify-between mb-4">
                        <div className="w-12 h-12 rounded-lg bg-red-100 flex items-center justify-center">
                            <AlertCircle className="w-6 h-6 text-red-600" />
                        </div>
                        <span className="text-sm font-medium text-gray-500">Critical</span>
                    </div>
                    <div className="text-3xl font-bold text-gray-900">{metrics.criticalIncidents}</div>
                    <p className="text-sm text-gray-600 mt-1">
                        {criticalPercentage}% of total
                    </p>
                </div>

                {/* Average Time to Close */}
                <div className="bg-white rounded-xl border border-gray-200 p-6">
                    <div className="flex items-center justify-between mb-4">
                        <div className="w-12 h-12 rounded-lg bg-green-100 flex items-center justify-center">
                            <Clock className="w-6 h-6 text-green-600" />
                        </div>
                        <span className="text-sm font-medium text-gray-500">Avg. Close Time</span>
                    </div>
                    <div className="text-3xl font-bold text-gray-900">{metrics.averageTimeToClose}</div>
                    <p className="text-sm text-gray-600 mt-1">Days to resolution</p>
                </div>
            </div>

            <div className="grid lg:grid-cols-2 gap-6 mb-8">
                {/* Incidents by Status */}
                <div className="bg-white rounded-xl border border-gray-200 p-6">
                    <h2 className="text-lg font-semibold text-gray-900 mb-6">Incidents by Status</h2>
                    <div className="space-y-4">
                        {Object.entries(metrics.incidentsByStatus).map(([status, count]) => {
                            const percentage = metrics.totalIncidents > 0
                                ? Math.round((count / metrics.totalIncidents) * 100)
                                : 0;

                            const colors = {
                                'Open': 'bg-blue-500',
                                'Investigating': 'bg-purple-500',
                                'Analysis': 'bg-indigo-500',
                                'Action': 'bg-amber-500',
                                'Verification': 'bg-cyan-500',
                                'Closed': 'bg-green-500',
                            };

                            const color = colors[status as keyof typeof colors] || 'bg-gray-500';

                            return (
                                <div key={status}>
                                    <div className="flex items-center justify-between mb-2">
                                        <span className="text-sm font-medium text-gray-700">{status}</span>
                                        <span className="text-sm font-semibold text-gray-900">
                                            {count} ({percentage}%)
                                        </span>
                                    </div>
                                    <div className="w-full bg-gray-200 rounded-full h-2">
                                        <div
                                            className={`${color} h-2 rounded-full transition-all duration-500`}
                                            style={{ width: `${percentage}%` }}
                                        ></div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Incidents by Severity */}
                <div className="bg-white rounded-xl border border-gray-200 p-6">
                    <h2 className="text-lg font-semibold text-gray-900 mb-6">Incidents by Severity</h2>
                    <div className="space-y-4">
                        {Object.entries(metrics.incidentsBySeverity).map(([severity, count]) => {
                            const percentage = metrics.totalIncidents > 0
                                ? Math.round((count / metrics.totalIncidents) * 100)
                                : 0;

                            const colors = {
                                'Critical': 'bg-red-500',
                                'Major': 'bg-orange-500',
                                'Minor': 'bg-yellow-500',
                                'Cosmetic': 'bg-gray-500',
                            };

                            const color = colors[severity as keyof typeof colors] || 'bg-gray-500';

                            return (
                                <div key={severity}>
                                    <div className="flex items-center justify-between mb-2">
                                        <span className="text-sm font-medium text-gray-700">{severity}</span>
                                        <span className="text-sm font-semibold text-gray-900">
                                            {count} ({percentage}%)
                                        </span>
                                    </div>
                                    <div className="w-full bg-gray-200 rounded-full h-2">
                                        <div
                                            className={`${color} h-2 rounded-full transition-all duration-500`}
                                            style={{ width: `${percentage}%` }}
                                        ></div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* Top Failure Modes (Pareto) */}
            <div className="bg-white rounded-xl border border-gray-200 p-6 mb-8">
                <h2 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-2">
                    <Target className="w-5 h-5" />
                    Top 10 Failure Modes (Pareto Analysis)
                </h2>

                {metrics.topFailureModes.length === 0 ? (
                    <p className="text-gray-500 text-center py-8">
                        No failure modes recorded yet
                    </p>
                ) : (
                    <div className="space-y-3">
                        {metrics.topFailureModes.map((item, index) => {
                            const maxCount = metrics.topFailureModes[0]?.count || 1;
                            const percentage = Math.round((item.count / maxCount) * 100);

                            return (
                                <div key={index} className="flex items-center gap-4">
                                    <div className="w-8 text-center font-semibold text-gray-500">
                                        #{index + 1}
                                    </div>
                                    <div className="flex-1">
                                        <div className="flex items-center justify-between mb-1">
                                            <span className="text-sm font-medium text-gray-700">
                                                {item.mode}
                                            </span>
                                            <span className="text-sm font-semibold text-gray-900">
                                                {item.count} incidents
                                            </span>
                                        </div>
                                        <div className="w-full bg-gray-200 rounded-full h-2">
                                            <div
                                                className="bg-blue-500 h-2 rounded-full transition-all duration-500"
                                                style={{ width: `${percentage}%` }}
                                            ></div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Monthly Trend */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-2">
                    <TrendingUp className="w-5 h-5" />
                    12-Month Trend
                </h2>

                <div className="h-64 flex items-end justify-between gap-2">
                    {metrics.monthlyTrend.map((item, index) => {
                        const maxCount = Math.max(...metrics.monthlyTrend.map(m => m.count), 1);
                        const heightPercentage = (item.count / maxCount) * 100;

                        return (
                            <div key={index} className="flex-1 flex flex-col items-center gap-2">
                                <div className="relative w-full flex items-end justify-center" style={{ height: '200px' }}>
                                    <div
                                        className="w-full bg-blue-500 rounded-t hover:bg-blue-600 transition-colors cursor-pointer relative group"
                                        style={{ height: `${heightPercentage}%` }}
                                    >
                                        <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity bg-gray-900 text-white text-xs px-2 py-1 rounded whitespace-nowrap">
                                            {item.count} incidents
                                        </div>
                                    </div>
                                </div>
                                <span className="text-xs text-gray-600 transform -rotate-45 origin-left mt-2">
                                    {item.month}
                                </span>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Empty State */}
            {metrics.totalIncidents === 0 && (
                <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
                    <AlertTriangle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">No Data Yet</h3>
                    <p className="text-gray-600">
                        Start logging FRACAS incidents to see analytics and insights here
                    </p>
                </div>
            )}
        </div>
    );
};

export default AnalyticsDashboard;
